import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { ArrowLeft, Heart, Star, Calendar, Sparkles, TrendingUp, Search } from 'lucide-react';
import api from '@/lib/api';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { MemorySearch } from '@/components/MemorySearch';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { getCSSVariable } from '@/lib/design-utils';
import { PageLayout } from '@/layouts';
import { VStack, HStack, Grid } from '@/components/core';

interface EmotionalBonds {
  trust: number;
  affection: number;
  comfort: number;
  attraction: number;
}

interface Milestone {
  name: string;
  achieved_at: string;
  description: string;
}

interface SpecialMoment {
  moment: string;
  timestamp: string;
  emotional_impact: string;
}

interface RelationshipData {
  relationship_level: number;
  level_name: string;
  emotional_bonds: EmotionalBonds;
  current_mood: string;
  milestones: Milestone[];
  special_moments: SpecialMoment[];
  personality_insights: string[];
  shared_interests: string[];
}

interface Character {
  id: number;
  name: string;
  description: string;
  profile_image: string;
}

const moodEmojis: Record<string, string> = {
  happy: '😊',
  excited: '🥰',
  playful: '😄',
  flirty: '😘',
  thoughtful: '🤔',
  relaxed: '😌',
  passionate: '❤️‍🔥',
  curious: '🧐',
  affectionate: '🥺',
  confident: '😎',
};

export default function CharacterRelationship(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast: _toast } = useToast();
  const { t } = useTranslation('characters');
  const [character, setCharacter] = useState<Character | null>(null);
  const [relationship, setRelationship] = useState<RelationshipData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMemorySearch, setShowMemorySearch] = useState(false);
  const [selectedMemory, setSelectedMemory] = useState<{
    created_at?: string;
    content?: string;
    context?: string;
    emotional_context?: string;
    similarity_score?: number;
  } | null>(null);

  useEffect((): void => {
    void fetchData();
  }, [id]);

  const fetchData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');

      // Fetch character data
      const charResponse = await api.get(`/api/v1/characters/${id}`);
      setCharacter(charResponse.data);

      // Fetch relationship data
      const relResponse = await api.get(`/api/v1/memory/character/${id}/relationship`);
      setRelationship(relResponse.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(t('relationship.loadError'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLayout maxWidth="xl" padding="md">
        <VStack gap="lg">
          {/* Header skeleton */}
          <HStack justify="between" fullWidth>
            <Skeleton className="h-10 w-32 bg-white/10" />
            <Skeleton className="h-10 w-40 bg-white/10" />
          </HStack>

          {/* Character Info Header skeleton */}
          <Card>
            <CardContent className="p-token-lg">
              <HStack gap="md" align="start">
                <Skeleton className="h-20 w-20 rounded-full bg-white/10" />
                <VStack gap="sm" className="flex-1">
                  <Skeleton className="h-8 w-48 bg-white/10" />
                  <Skeleton className="h-4 w-full max-w-md bg-white/10" />
                  <HStack gap="md" wrap className="mt-token-sm">
                    <Skeleton className="h-6 w-32 bg-white/10" />
                    <Skeleton className="h-6 w-24 bg-white/10" />
                  </HStack>
                </VStack>
              </HStack>
            </CardContent>
          </Card>

          {/* Two-column grid skeleton */}
          <Grid cols={1} colsMd={2} gap="lg">
            {/* Radar chart card skeleton */}
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40 bg-white/10" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[300px] w-full bg-white/10" />
              </CardContent>
            </Card>

            {/* Right column: Personality Insights & Shared Interests skeleton */}
            <VStack gap="lg">
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-44 bg-white/10" />
                </CardHeader>
                <CardContent>
                  <VStack gap="sm">
                    <Skeleton className="h-4 w-full bg-white/10" />
                    <Skeleton className="h-4 w-5/6 bg-white/10" />
                    <Skeleton className="h-4 w-4/5 bg-white/10" />
                  </VStack>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-36 bg-white/10" />
                </CardHeader>
                <CardContent>
                  <HStack gap="sm" wrap>
                    <Skeleton className="h-6 w-20 bg-white/10" />
                    <Skeleton className="h-6 w-24 bg-white/10" />
                    <Skeleton className="h-6 w-16 bg-white/10" />
                    <Skeleton className="h-6 w-28 bg-white/10" />
                  </HStack>
                </CardContent>
              </Card>
            </VStack>
          </Grid>

          {/* Milestones timeline skeleton */}
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32 bg-white/10" />
            </CardHeader>
            <CardContent>
              <VStack gap="md">
                {[1, 2, 3].map((i) => (
                  <HStack key={i} gap="md" className="pb-token-md border-b last:border-0">
                    <Skeleton className="h-12 w-12 rounded-full flex-shrink-0 bg-white/10" />
                    <VStack gap="xs" className="flex-1">
                      <Skeleton className="h-5 w-40 bg-white/10" />
                      <Skeleton className="h-4 w-full max-w-lg bg-white/10" />
                      <Skeleton className="h-3 w-28 bg-white/10" />
                    </VStack>
                  </HStack>
                ))}
              </VStack>
            </CardContent>
          </Card>

          {/* Special moments grid skeleton */}
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-36 bg-white/10" />
            </CardHeader>
            <CardContent>
              <Grid cols={1} colsMd={2} gap="md">
                {[1, 2, 3, 4].map((i) => (
                  <VStack key={i} gap="sm" className="bg-white/10 rounded-token-button p-token-md">
                    <Skeleton className="h-4 w-full bg-white/10" />
                    <HStack justify="between" fullWidth>
                      <Skeleton className="h-3 w-28 bg-white/10" />
                      <Skeleton className="h-5 w-16 bg-white/10" />
                    </HStack>
                  </VStack>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </VStack>
      </PageLayout>
    );
  }

  if (error || !character || !relationship) {
    return (
      <PageLayout maxWidth="xl" padding="md">
        <VStack gap="md">
          <Button
            variant="ghost"
            onClick={() => navigate('/characters')}
          >
            <ArrowLeft className="mr-token-sm h-4 w-4" />
            {t('buttons.back', { ns: 'common' })}
          </Button>
          <Card>
            <CardContent className="p-token-xl text-center">
              <p className="text-error">{error || t('relationship.dataLoadError')}</p>
            </CardContent>
          </Card>
        </VStack>
      </PageLayout>
    );
  }

  // Prepare radar chart data
  const radarData = [
    {
      metric: t('relationship.trust'),
      value: relationship.emotional_bonds.trust,
      fullMark: 100,
    },
    {
      metric: t('relationship.affection'),
      value: relationship.emotional_bonds.affection,
      fullMark: 100,
    },
    {
      metric: t('relationship.comfort'),
      value: relationship.emotional_bonds.comfort,
      fullMark: 100,
    },
    {
      metric: t('relationship.attraction'),
      value: relationship.emotional_bonds.attraction,
      fullMark: 100,
    },
  ];

  const getMoodEmoji = (mood: string): string => {
    return moodEmojis[mood.toLowerCase()] || '😊';
  };

  const getLevelColor = (level: number): string => {
    if (level >= 80) return 'text-brand-primary-dark';
    if (level >= 60) return 'text-brand-secondary';
    if (level >= 40) return 'text-info';
    if (level >= 20) return 'text-success';
    return 'text-muted-foreground';
  };

  return (
    <PageLayout maxWidth="xl" padding="md">
      <VStack gap="lg">
        {/* Header */}
        <HStack justify="between" fullWidth>
          <Button
            variant="ghost"
            onClick={() => navigate(`/chats/${character.id}`)}
          >
            <ArrowLeft className="mr-token-sm h-4 w-4" />
            {t('relationship.backToChat')}
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowMemorySearch(true)}
          >
            <Search className="mr-token-sm h-4 w-4" />
            {t('relationship.searchMemories')}
          </Button>
        </HStack>

        {/* Character Info Header */}
        <Card>
          <CardContent className="p-token-lg">
            <HStack gap="md" align="start">
              <Avatar className="h-20 w-20">
                <AvatarImage src={character.profile_image} alt={character.name} />
                <AvatarFallback>{character.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <VStack gap="sm" className="flex-1">
                <h1 className="text-2xl font-bold">{character.name}</h1>
                <p className="text-muted-foreground">{character.description}</p>
                <HStack gap="md" wrap className="mt-token-sm">
                  <Badge variant="secondary" className={getLevelColor(relationship.relationship_level)}>
                    <Heart className="w-3 h-3 mr-token-xs" />
                    Level {relationship.relationship_level} - {relationship.level_name}
                  </Badge>
                  <HStack gap="sm" align="center">
                    <span className="text-sm text-muted-foreground">{t('relationship.currentMood')}:</span>
                    <span className="text-2xl">{getMoodEmoji(relationship.current_mood ?? '')}</span>
                    <span className="text-sm font-medium">{relationship.current_mood ?? ''}</span>
                  </HStack>
                </HStack>
              </VStack>
            </HStack>
          </CardContent>
        </Card>

        <Grid cols={1} colsMd={2} gap="lg">
          {/* Emotional Bonds Radar Chart */}
          <Card>
            <CardHeader>
              <CardTitle>
                <HStack gap="sm" align="center">
                  <Heart className="w-5 h-5 text-brand-primary" />
                  {t('relationship.emotionalBond')}
                </HStack>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="var(--color-border)" />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 100]}
                    tickCount={5}
                  />
                  <Radar
                    name={t('relationship.values')}
                    dataKey="value"
                    stroke={getCSSVariable('--color-rarity-epic')}
                    fill={getCSSVariable('--color-rarity-epic')}
                    fillOpacity={0.6}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Personality Insights & Shared Interests */}
          <VStack gap="lg">
            <Card>
              <CardHeader>
                <CardTitle>
                  <HStack gap="sm" align="center">
                    <Sparkles className="w-5 h-5 text-brand-secondary" />
                    {t('relationship.personalityInsights')}
                  </HStack>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <VStack gap="sm">
                  {relationship.personality_insights.map((insight, index) => (
                    <HStack key={index} gap="sm" align="start">
                      <span className="text-brand-secondary mt-token-xs">-</span>
                      <span className="text-sm">{insight}</span>
                    </HStack>
                  ))}
                </VStack>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <HStack gap="sm" align="center">
                    <Star className="w-5 h-5 text-warning" />
                    {t('relationship.sharedInterests')}
                  </HStack>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <HStack gap="sm" wrap>
                  {relationship.shared_interests.map((interest, index) => (
                    <Badge key={index} variant="secondary">
                      {interest}
                    </Badge>
                  ))}
                </HStack>
              </CardContent>
            </Card>
          </VStack>
        </Grid>

        {/* Milestones Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>
              <HStack gap="sm" align="center">
                <TrendingUp className="w-5 h-5 text-success" />
                {t('relationship.milestones')}
              </HStack>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VStack gap="md">
              {relationship.milestones.map((milestone, index) => (
                <HStack key={index} gap="md" className="pb-token-md border-b last:border-0">
                  <div className="flex-shrink-0 w-12 h-12 bg-success/20 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-success" />
                  </div>
                  <VStack gap="xs" className="flex-1">
                    <h4 className="font-semibold">{milestone.name}</h4>
                    <p className="text-sm text-muted-foreground">{milestone.description}</p>
                    <p className="text-xs text-muted-foreground/70">
                      {format(new Date(milestone.achieved_at), 'dd. MMMM yyyy', { locale: de })}
                    </p>
                  </VStack>
                </HStack>
              ))}
            </VStack>
          </CardContent>
        </Card>

        {/* Special Moments */}
        <Card>
          <CardHeader>
            <CardTitle>
              <HStack gap="sm" align="center">
                <Calendar className="w-5 h-5 text-brand-primary" />
                {t('relationship.specialMoments')}
              </HStack>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Grid cols={1} colsMd={2} gap="md">
              {relationship.special_moments.map((moment, index) => (
                <VStack key={index} gap="sm" className="bg-brand-primary/10 rounded-token-button p-token-md">
                  <p className="font-medium text-sm">{moment.moment}</p>
                  <HStack justify="between" fullWidth>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(moment.timestamp), 'dd.MM.yyyy HH:mm', { locale: de })}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {moment.emotional_impact}
                    </Badge>
                  </HStack>
                </VStack>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Memory Search Modal */}
        {showMemorySearch && character && (
          <Dialog open={showMemorySearch} onOpenChange={setShowMemorySearch}>
            <DialogContent className="max-w-3xl">
              <MemorySearch
                characterId={character.id}
                onMemorySelect={(memory) => {
                  setSelectedMemory(memory);
                  setShowMemorySearch(false);
                }}
                isModal={false}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Selected Memory Dialog */}
        {selectedMemory && (
          <Dialog open={!!selectedMemory} onOpenChange={() => setSelectedMemory(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('relationship.memory')}</DialogTitle>
                <DialogDescription>
                  {format(new Date(selectedMemory.created_at), 'dd. MMMM yyyy, HH:mm', { locale: de })}
                </DialogDescription>
              </DialogHeader>
              <VStack gap="md">
                <VStack gap="xs">
                  <h4 className="font-semibold">{t('relationship.content')}:</h4>
                  <p className="text-foreground/80">{selectedMemory.content}</p>
                </VStack>
                {selectedMemory.context && (
                  <VStack gap="xs">
                    <h4 className="font-semibold">{t('relationship.context')}:</h4>
                    <p className="text-muted-foreground">{selectedMemory.context}</p>
                  </VStack>
                )}
                {selectedMemory.emotional_context && (
                  <VStack gap="xs">
                    <h4 className="font-semibold">{t('relationship.emotionalContext')}:</h4>
                    <Badge variant="secondary" className="text-sm">
                      {selectedMemory.emotional_context}
                    </Badge>
                  </VStack>
                )}
                {selectedMemory.similarity_score && (
                  <VStack gap="xs">
                    <h4 className="font-semibold">{t('relationship.similarity')}:</h4>
                    <p className="text-sm text-muted-foreground">
                      {Math.round(selectedMemory.similarity_score * 100)}%
                    </p>
                  </VStack>
                )}
              </VStack>
            </DialogContent>
          </Dialog>
        )}
      </VStack>
    </PageLayout>
  );
}